/**
 * The card folder (asked for 2026-10-10: "save the work as a project on the
 * PC, so nothing is lost"). The person picks a folder once - Documents\FoldMyCard,
 * say - and from then on every card is also kept there as its own project file,
 * "<title> (<id>).foldmycard" (card-file.ts), written a moment after each change.
 * If the browser's storage is ever cleared, choosing the folder again brings
 * every card back; a folder inside OneDrive, Dropbox or iCloud Drive carries the
 * cards to the person's other computers too - through their own cloud, never
 * our server.
 *
 * Only browsers with the File System Access API can do this (Chrome and Edge
 * on computers, recent Chrome on Android). Elsewhere - Safari on iPhone, iPad
 * and Mac, and Firefox - `folderSupported()` is false and the home page offers
 * "Back up all my cards" instead.
 *
 * Keeping the two in step (`sync`, when the folder is chosen, when the page
 * opens and when it comes back into view): a card only here is written to the
 * folder; a card only in the folder is put back here; a card in both keeps the
 * newer copy, on both sides. A card deleted here while the folder could not be
 * reached is remembered, and its file removed at the next sync - so it does
 * not come back.
 */
import { getMeta, setMeta, deleteMeta, onAccountChange } from './db.ts';
import { onCardEvent, cardDates } from './store.ts';
import { CARD_EXTENSION, peekCardFile, readCardFile, saveCardFile, takeCard, type KnowsTemplate } from './card-file.ts';

/* The parts of the File System Access API that TypeScript's DOM types leave out. */
type Permission = 'granted' | 'denied' | 'prompt';
interface FolderHandle extends FileSystemDirectoryHandle {
  queryPermission(o: { mode: 'readwrite' }): Promise<Permission>;
  requestPermission(o: { mode: 'readwrite' }): Promise<Permission>;
}
declare global {
  interface Window {
    showDirectoryPicker?: (o?: { id?: string; mode?: 'read' | 'readwrite'; startIn?: string }) => Promise<FileSystemDirectoryHandle>;
  }
}

export const folderSupported = () => typeof window.showDirectoryPicker === 'function';

export type FolderStatus = 'unsupported' | 'off' | 'needs-permission' | 'saving' | 'saved' | 'error';
export interface FolderState { status: FolderStatus; name?: string; error?: string; at?: string }

let state: FolderState = { status: folderSupported() ? 'off' : 'unsupported' };
const listeners = new Set<(s: FolderState) => void>();
const set = (next: FolderState) => {
  state = next;
  for (const f of listeners) f(state);
};
export const folderState = () => state;
export const onFolderState = (f: (s: FolderState) => void) => { listeners.add(f); return () => { listeners.delete(f); }; };

const FOLDER = 'folder';
const DELETED = 'folderDeleted';
/** When the cards were last copied somewhere safe - a backup file, or the folder. */
export const LAST_BACKUP = 'lastBackupAt';

let handle: FolderHandle | null = null;
/** The card design check a card from the folder must pass (api.template); set by `startFolder`. */
let knows: KnowsTemplate = async () => true;
/** Each card's file name in the folder, by card id, as last seen or written. */
const names = new Map<string, string>();
const timers = new Map<string, number>();
/** Cards being put back from the folder right now: their "changed" is not news to the folder. */
const fromFolder = new Set<string>();

const isCardFile = (name: string) => name.endsWith(`.${CARD_EXTENSION}`);
const message = (err: unknown) => (err as Error)?.message || String(err);

/** The card files in the folder: name -> the card's id and last change. */
async function listFolder(h: FolderHandle) {
  const out = new Map<string, { name: string; updatedAt: string; file: File }>();
  for await (const entry of h.values()) {
    if (entry.kind !== 'file' || !isCardFile(entry.name)) continue;
    const file = await (entry as FileSystemFileHandle).getFile();
    const peek = await peekCardFile(file);
    if (!peek) continue;
    const seen = out.get(peek.id);
    if (!seen || seen.updatedAt < peek.updatedAt) out.set(peek.id, { name: entry.name, updatedAt: peek.updatedAt, file });
  }
  return out;
}

async function writeCard(h: FolderHandle, id: string) {
  const card = await saveCardFile(id);
  const fh = await h.getFileHandle(card.name, { create: true });
  const w = await fh.createWritable();
  await w.write(card.blob);
  await w.close();
  const old = names.get(id);
  if (old && old !== card.name) await h.removeEntry(old).catch(() => {});     // renamed
  names.set(id, card.name);
}

async function take(file: File, id: string) {
  fromFolder.add(id);
  try {
    await takeCard(await readCardFile(file), knows);
  } finally {
    fromFolder.delete(id);
  }
}

/** Bring this device and the folder into step (see the top of this file). */
export async function sync(): Promise<void> {
  const h = handle;
  if (!h) return;
  set({ ...state, status: 'saving' });
  try {
    const inFolder = await listFolder(h);
    const here = await cardDates();
    const deleted = (await getMeta<Record<string, string>>(DELETED)) ?? {};
    names.clear();
    for (const [id, f] of inFolder) names.set(id, f.name);

    for (const [id, f] of inFolder) {
      if (here.has(id)) continue;
      if (deleted[id] && deleted[id] >= f.updatedAt) {
        await h.removeEntry(f.name).catch(() => {});
        names.delete(id);
        continue;
      }
      await take(f.file, id);
    }
    for (const [id, updatedAt] of here) {
      const f = inFolder.get(id);
      if (!f || f.updatedAt < updatedAt) await writeCard(h, id);
      else if (f.updatedAt > updatedAt) await take(f.file, id);
    }
    await deleteMeta(DELETED);
    const at = new Date().toISOString();
    await setMeta(LAST_BACKUP, at);
    set({ status: 'saved', name: h.name, at });
  } catch (err) {
    if ((err as Error)?.name === 'NotAllowedError') set({ status: 'needs-permission', name: h.name });
    else set({ status: 'error', name: h.name, error: message(err) });
  }
}

/** A card changed: write it to the folder in a moment (typing changes it many times a second). */
function later(id: string) {
  window.clearTimeout(timers.get(id));
  timers.set(id, window.setTimeout(() => {
    timers.delete(id);
    const h = handle;
    if (!h || state.status === 'needs-permission') return;
    set({ ...state, status: 'saving' });
    writeCard(h, id)
      .then(async () => {
        if (timers.size) return;
        const at = new Date().toISOString();
        await setMeta(LAST_BACKUP, at);
        set({ status: 'saved', name: h.name, at });
      })
      .catch((err) => {
        if ((err as Error)?.name === 'NotAllowedError') set({ status: 'needs-permission', name: h.name });
        else set({ status: 'error', name: h.name, error: message(err) });
      });
  }, 1500));
}

async function removeCard(id: string) {
  window.clearTimeout(timers.get(id));
  timers.delete(id);
  const h = handle;
  const name = names.get(id);
  if (h && name && state.status !== 'needs-permission') {
    try {
      await h.removeEntry(name);
      names.delete(id);
      return;
    } catch { /* remembered below */ }
  }
  if (!h) return;
  // the folder cannot be reached now: remember, so the next sync removes the file instead of bringing the card back
  const deleted = (await getMeta<Record<string, string>>(DELETED)) ?? {};
  await setMeta(DELETED, { ...deleted, [id]: new Date().toISOString() });
}

onCardEvent((e) => {
  if (!handle || fromFolder.has(e.projectId)) return;
  if (e.kind === 'deleted') void removeCard(e.projectId);
  else later(e.projectId);
});
onAccountChange(() => {
  for (const t of timers.values()) window.clearTimeout(t);
  timers.clear();
  names.clear();
  handle = null;
  set({ status: folderSupported() ? 'off' : 'unsupported' });
});

let lastSync = 0;
/** Coming back to the page: pick up what changed in the folder meanwhile (another computer, through a cloud drive). */
function onReturn() {
  if (document.visibilityState !== 'visible' || !handle || state.status !== 'saved') return;
  if (Date.now() - lastSync < 15_000) return;
  lastSync = Date.now();
  void sync();
}
document.addEventListener('visibilitychange', onReturn);

/** After the account's store is open (main.tsx): the folder chosen before, if there is one and the browser still allows it. */
export async function startFolder(knowsTemplate: KnowsTemplate): Promise<void> {
  knows = knowsTemplate;
  if (!folderSupported()) return;
  const saved = await getMeta<FolderHandle>(FOLDER).catch(() => undefined);
  if (!saved) return set({ status: 'off' });
  handle = saved;
  const p = await saved.queryPermission({ mode: 'readwrite' }).catch(() => 'prompt' as Permission);
  if (p === 'granted') {
    lastSync = Date.now();
    await sync();
  } else {
    set({ status: 'needs-permission', name: saved.name });
  }
}

/** Use `h` as this account's card folder from now on (the picker's answer, or another folder handle). */
export async function setFolder(h: FileSystemDirectoryHandle): Promise<void> {
  handle = h as unknown as FolderHandle;
  await setMeta(FOLDER, h);
  lastSync = Date.now();
  await sync();
}

/** Let the person pick the folder. Must run from a tap or click. */
export async function chooseFolder(): Promise<void> {
  const h = await window.showDirectoryPicker!({ id: 'foldmycard-cards', mode: 'readwrite', startIn: 'documents' });
  await setFolder(h);
}

/** Ask again for the folder chosen before (browsers ask once per visit unless told to always allow). Must run from a tap. */
export async function reconnectFolder(): Promise<void> {
  if (!handle) return;
  const p = await handle.requestPermission({ mode: 'readwrite' });
  if (p === 'granted') await sync();
  else set({ status: 'needs-permission', name: handle.name });
}

/** Stop saving to the folder. The files already there stay. */
export async function forgetFolder(): Promise<void> {
  handle = null;
  names.clear();
  await deleteMeta(FOLDER);
  set({ status: 'off' });
}
